import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { CRITICALITIES } from "../../Issues/criticality";
import { generateUrlForWorkspace } from "../common";
import { LEVELS } from "./criticality";

const PADDING_TOP = 6;
const PADDING_BOTTOM = 8;
const LABEL_HEIGHT = 16;
const LABEL_GAP = 4;
const TILE_HEIGHT = 50;

/**
 * The workspace's Critical, High and Medium counts, opening its Issues page; `counts` is absent when
 * its issue analysis has not run, and the panel keeps its height but is empty while `loading`.
 */
const IssuesPanel = ({ workspaceNumber, loading, counts }) => (
    <Section>
        {loading ? (
            <Empty aria-hidden />
        ) : (
            <Panel to={`${generateUrlForWorkspace(workspaceNumber)}/issues`}>
                <Label>Issues</Label>
                {counts ? (
                    <Tiles>
                        {LEVELS.map((level) => {
                            const colours = counts[level] > 0 ? CRITICALITIES[level] : CRITICALITIES.low;
                            return (
                                <Tile key={level} $colours={colours}>
                                    <Count>{counts[level].toLocaleString()}</Count>
                                    <Name>
                                        <Dot $color={colours.color} />
                                        {CRITICALITIES[level].label}
                                    </Name>
                                </Tile>
                            );
                        })}
                    </Tiles>
                ) : (
                    <NotRun>Issue analysis has not run</NotRun>
                )}
            </Panel>
        )}
    </Section>
);

const Section = styled.div`
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid #e2e8f0;
`;

const Panel = styled(Link)`
    && {
        display: block;
        margin: 0 -8px;
        padding: ${PADDING_TOP}px 8px ${PADDING_BOTTOM}px;
        border-radius: 8px;
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        transition: background 150ms ease;
    }
    &&:hover, &&:focus {
        background: #f1f5f9;
        text-decoration: none;
        color: inherit;
    }
`;

const Empty = styled.div`
    height: ${PADDING_TOP + LABEL_HEIGHT + LABEL_GAP + TILE_HEIGHT + PADDING_BOTTOM}px;
`;

const Label = styled.div`
    height: ${LABEL_HEIGHT}px;
    margin-bottom: ${LABEL_GAP}px;
    font-size: 0.7rem;
    line-height: ${LABEL_HEIGHT}px;
    font-weight: 500;
    color: #6b7280;
`;

const Tiles = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
`;

const Tile = styled.div`
    box-sizing: border-box;
    height: ${TILE_HEIGHT}px;
    padding: 6px 8px;
    border-radius: 6px;
    background: ${({ $colours }) => $colours.background};
    color: ${({ $colours }) => $colours.ink};
`;

const Count = styled.div`
    font-size: 20px;
    line-height: 24px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
`;

const Name = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    line-height: 16px;
`;

const Dot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 999px;
    flex-shrink: 0;
    background: ${({ $color }) => $color};
`;

const NotRun = styled.div`
    display: flex;
    align-items: center;
    height: ${TILE_HEIGHT}px;
    font-size: 0.8rem;
    color: #9ca3af;
`;

export default IssuesPanel;
